import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface User {
  id: string;
  email: string;
  user_name: string;
}

interface MentionState {
  isActive: boolean;
  searchText: string;
  startIndex: number;
  endIndex: number;
}

interface UseMentionsReturn {
  mentionState: MentionState;
  filteredUsers: User[];
  selectedIndex: number;
  isLoading: boolean;
  handleTextChange: (text: string, cursorPosition: number) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => boolean;
  selectUser: (user: User) => void;
  setSelectedIndex: (index: number) => void;
  closeMentions: () => void;
}

export const useMentions = (
  value: string,
  onChange: (value: string) => void,
  onMentionUser?: (email: string) => void
): UseMentionsReturn => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [mentionState, setMentionState] = useState<MentionState>({
    isActive: false,
    searchText: '',
    startIndex: -1,
    endIndex: -1,
  });
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);

  // Load users from Supabase
  useEffect(() => {
    const loadUsers = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('users')
          .select('id, email, user_name')
          .order('email');

        if (error) throw error;
        setUsers(data || []);
      } catch (error) {
        console.error('Error loading users:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUsers();
  }, []);

  // Filter users based on mention search text
  useEffect(() => {
    if (!mentionState.isActive || !mentionState.searchText) {
      setFilteredUsers([]);
      setSelectedIndex(-1);
      return;
    }

    const filtered = users.filter(user => 
      user.email.toLowerCase().includes(mentionState.searchText.toLowerCase()) ||
      (user.user_name && user.user_name.toLowerCase().includes(mentionState.searchText.toLowerCase()))
    ).slice(0, 10); // Limit to 10 results

    setFilteredUsers(filtered);
    setSelectedIndex(filtered.length > 0 ? 0 : -1);
  }, [mentionState.searchText, mentionState.isActive, users]);

  const findMentionContext = useCallback((text: string, cursorPosition: number) => {
    // Find the last @ before cursor position
    let atIndex = -1;
    for (let i = cursorPosition - 1; i >= 0; i--) {
      if (text[i] === '@') {
        atIndex = i;
        break;
      }
      // Stop at whitespace, newline, or beginning
      if (text[i] === ' ' || text[i] === '\n') {
        break;
      }
    }

    if (atIndex === -1) return null;

    // Check if @ is at start of text or preceded by whitespace
    const prevChar = atIndex > 0 ? text[atIndex - 1] : ' ';
    if (prevChar !== ' ' && prevChar !== '\n') return null;

    // Find the end of the mention (next whitespace or end of text)
    let endIndex = cursorPosition;
    for (let i = atIndex + 1; i < text.length; i++) {
      if (text[i] === ' ' || text[i] === '\n') {
        endIndex = i;
        break;
      }
      if (i === cursorPosition) {
        endIndex = i;
        break;
      }
    }

    const searchText = text.slice(atIndex + 1, endIndex);
    
    return {
      startIndex: atIndex,
      endIndex,
      searchText,
    };
  }, []);

  const handleTextChange = useCallback((text: string, cursorPosition: number) => {
    const mentionContext = findMentionContext(text, cursorPosition);
    
    if (mentionContext) {
      setMentionState({
        isActive: true,
        searchText: mentionContext.searchText,
        startIndex: mentionContext.startIndex,
        endIndex: mentionContext.endIndex,
      });
    } else {
      setMentionState({
        isActive: false,
        searchText: '',
        startIndex: -1,
        endIndex: -1,
      });
    }
  }, [findMentionContext]);

  const selectUser = useCallback((user: User) => {
    if (!mentionState.isActive) return;

    const beforeMention = value.slice(0, mentionState.startIndex);
    const afterMention = value.slice(mentionState.endIndex);
    const newText = beforeMention + `@${user.user_name || user.email}` + afterMention;
    
    onChange(newText);
    
    // Call the callback to add user to email list
    if (onMentionUser) {
      onMentionUser(user.email);
    }

    // Close mentions
    setMentionState({
      isActive: false,
      searchText: '',
      startIndex: -1,
      endIndex: -1,
    });
    setSelectedIndex(-1);
  }, [mentionState, value, onChange, onMentionUser]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!mentionState.isActive || filteredUsers.length === 0) {
      return false; // Let default behavior handle
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredUsers.length - 1 ? prev + 1 : 0
        );
        return true;
        
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : filteredUsers.length - 1
        );
        return true;
        
      case 'Enter':
      case 'Tab':
        if (selectedIndex >= 0 && selectedIndex < filteredUsers.length) {
          e.preventDefault();
          selectUser(filteredUsers[selectedIndex]);
          return true;
        }
        break;
        
      case 'Escape':
        e.preventDefault();
        closeMentions();
        return true;
    }
    
    return false;
  }, [mentionState.isActive, filteredUsers, selectedIndex, selectUser]);

  const closeMentions = useCallback(() => {
    setMentionState({
      isActive: false,
      searchText: '',
      startIndex: -1,
      endIndex: -1,
    });
    setSelectedIndex(-1);
  }, []);

  return {
    mentionState,
    filteredUsers,
    selectedIndex,
    isLoading,
    handleTextChange,
    handleKeyDown,
    selectUser,
    setSelectedIndex,
    closeMentions,
  };
};